import type { ReactElement } from 'react';
import { useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Dialog } from '@opendoor/atoms/dialog';
import { Button } from 'platform/atoms';
import {
  setWriteModeEnabled,
  useImpersonatedCustomerName,
  useMutationsBlocked,
} from 'platform/readOnlyMutations';

type WriteModeConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The customer being viewed, already defaulted for the pre-lead case. */
  viewing: string;
};

/**
 * The state both entry points need: which customer is on screen, whether writes are blocked, and
 * the confirmation itself. Returning the element follows `useToast`, so a caller renders it
 * without owning the open state.
 */
export const useWriteMode = () => {
  const blocked = useMutationsBlocked();
  const name = useImpersonatedCustomerName();
  const [confirming, setConfirming] = useState(false);
  const viewing = name || 'this customer';

  return {
    blocked,
    viewing,
    /** Turning writes on asks first; turning them back off is immediate. */
    requestWriteMode: () => setConfirming(true),
    turnOffWriteMode: () => setWriteModeEnabled(false),
    confirmDialog: (
      <WriteModeConfirmDialog open={confirming} onOpenChange={setConfirming} viewing={viewing} />
    ),
  };
};

/**
 * Shared by the desktop banner and the mobile toggle, which are two entry points to the same
 * decision.
 */
const WriteModeConfirmDialog = ({
  open,
  onOpenChange,
  viewing,
}: WriteModeConfirmDialogProps): ReactElement => (
  <Dialog.Root role="alertdialog" open={open} onOpenChange={onOpenChange}>
    <Dialog>
      <Col analyticsName="cake-impersonation-write-mode-confirm" trackImpression>
        <Dialog.Title className="pb-4 text-subheader-lg">Turn on write mode?</Dialog.Title>
        <Dialog.Description className="pb-6 text-body-md text-od-content-secondary">
          You are impersonating {viewing}. From now on, everything you change saves to their real
          account, including their offer.
        </Dialog.Description>
        <Row className="flex-col justify-end gap-3 md:flex-row">
          <Button
            variant="secondary"
            size="md"
            analyticsName="impersonation-write-mode-confirm-cancel"
            aria-label="Cancel"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="md"
            analyticsName="impersonation-write-mode-confirm-accept"
            aria-label="Turn it on"
            onClick={() => {
              setWriteModeEnabled(true);
              onOpenChange(false);
            }}
          >
            Turn it on
          </Button>
        </Row>
      </Col>
    </Dialog>
  </Dialog.Root>
);
